/* Role/permission helpers for the UI.
 *
 * The backend embeds each membership's effective RolePermission matrix into
 * /me (see apps/common/permissions.effective_permissions). Components use
 * `can()` to hide or disable controls the API would reject with 403, instead
 * of letting users discover their limits through errors.
 */

import type { AuthState, Membership } from "./auth";

export type PermAction = "view" | "edit" | "delete";

export function membershipFor(state: AuthState, tenantSlug: string): Membership | undefined {
  return state.memberships.find((m) => m.tenant.slug === tenantSlug);
}

export function can(
  state: AuthState,
  tenantSlug: string,
  section: string,
  action: PermAction = "view",
): boolean {
  const m = membershipFor(state, tenantSlug);
  if (!m) return state.user?.is_super_admin ?? false;
  if (!m.is_active) return false;
  if (m.role === "owner") return true;
  return m.permissions?.[section]?.[action] ?? false;
}

/* ── Plan entitlements (the subscription layer) ──────────────────────────
 * Orthogonal to `can()`: roles decide *who* may act, the plan decides *what
 * the tenant is allowed at all*. Mirrors apps.common.entitlements; gate UI on
 * these so users see an upgrade prompt instead of a 402. */

/** The six optional modules of the subscription constructor. */
export type ModuleFeature =
  | "public_page" | "warehouse" | "accounting" | "reports" | "dashboard_mgmt" | "partner_search";
const MODULE_FEATURES: readonly string[] = [
  "public_page", "warehouse", "accounting", "reports", "dashboard_mgmt", "partner_search",
];

export type Feature =
  | "pricing" | "orders" | "purchase_orders" | "publish_catalog" | "sales_pricing" | "accept_orders"
  | ModuleFeature;

export function isPro(state: AuthState, tenantSlug: string): boolean {
  const ent = membershipFor(state, tenantSlug)?.entitlements;
  if (!ent?.is_pro) return false;
  return ent.status !== "trialing" || (!!ent.trial_ends_at && Date.parse(ent.trial_ends_at) > Date.now());
}

/** May this company use `feature` right now? `/me` names every paid feature
 *  with its own `allowed` — a company on its own set ("custom") has some of
 *  them without being Pro, so the flag decides, not the plan. */
export function hasEntitlement(
  state: AuthState,
  tenantSlug: string,
  feature: Feature,
): boolean {
  const m = membershipFor(state, tenantSlug);
  if (!m) return state.user?.is_super_admin ?? false;
  if (feature === "orders" || feature === "purchase_orders") return true;
  const ent = m.entitlements;
  // A trial held in a cached session closes at its deadline, before /me is read again.
  if (ent?.is_pro && !isPro(state, tenantSlug)) return false;
  const flag = ent?.features?.[feature]?.allowed;
  if (flag !== undefined) return flag;
  // A session cached before modules were priced: modules stay open as they
  // were, selling follows the old Pro switch.
  return MODULE_FEATURES.includes(feature) ? true : isPro(state, tenantSlug);
}

/** The module locks (nav, ModuleGate): is this module part of the company's
 *  subscription? Same rule as `hasEntitlement`, named for the call sites. */
export function hasFeature(state: AuthState, tenantSlug: string, feature: ModuleFeature | Feature): boolean {
  return hasEntitlement(state, tenantSlug, feature);
}

/** V2 actions come exclusively from /me; the optional fallback is for v1 tenants. */
export function canAction(state: AuthState, slug: string, action: string, legacy?: [string, PermAction]): boolean {
  const member = membershipFor(state, slug);
  if (!member) return state.user?.is_super_admin ?? false;
  if (!member.is_active) return false;
  if (member.permission_version === 2) return member.is_active && (member.actions?.[action] ?? false);
  return legacy ? can(state, slug, legacy[0], legacy[1]) : member.role === "owner" || member.role === "manager";
}

const NAV_ACTIONS: Record<string, string[]> = {
  payments: ["payments.view"],
  accounting: ["accounting_reports.view"],
  reports: ["accounting_reports.view"],
  distribution: ["customers.view", "suppliers.view"],
  "search-requests": ["customers.view", "suppliers.view"],
  partners: ["customers.view", "suppliers.view"],
  "product-categories": ["categories.view"], products: ["products.view"],
  prices: ["purchase_prices.view", "sales_prices.view"],
  stock: ["stock.view", "stock.available"], inbound: ["receipts.view"], outbound: ["fulfillment.view"],
  orders: ["purchase_orders.view", "sales_orders.view", "fulfillment.view"],
  assortment: ["purchase_orders.view"],
  documents: ["accounting_documents.view"],
};

export function canNavigate(state: AuthState, slug: string, section: string): boolean {
  const member = membershipFor(state, slug);
  if (member?.permission_version !== 2) return true;
  const actions = NAV_ACTIONS[section];
  return !actions || actions.some(action => canAction(state, slug, action));
}

export function canSettingsTab(state: AuthState, slug: string, tab: string): boolean {
  const member = membershipFor(state, slug);
  if (member?.permission_version !== 2) return true;
  const actions: Record<string, string> = {
    general: "company_profile.view",
    "company-profile": "company_profile.view", "document-data": "document_requisites.view",
    "display-on-site": "company_branding.view", team: "team.view", roles: "permissions.view",
    // How prices are written — the old Prices → Настройки tab.
    "price-display": "sales_prices.view",
    // «Маркировка склада» is read by whoever reads the shelf.
    warehouse: "stock.view",
    // Read by whoever sends the logistics request; edited with the requisites.
    "transport-companies": "logistics_documents.view",
    roadmap: "roadmap.view", onboarding: "company_settings.edit",
  };
  if (tab === "tariff") return member.role === "owner";
  return !actions[tab] || canAction(state, slug, actions[tab]);
}

export function canRoute(state: AuthState, slug: string, section: string, child = ''): boolean {
  if (membershipFor(state, slug)?.permission_version !== 2) return true;
  if (section === 'account') return canSettingsTab(state,slug,child || 'company-profile');
  if (section === 'products' && child === 'new') return canAction(state,slug,'products.edit');
  if (section === 'products' && child === 'import') return canAction(state,slug,'products.import');
  if (section === 'orders' && child === 'new' || section === 'cart') return canAction(state,slug,'purchase_orders.edit');
  if (section === 'orders' && ['for-client','draft'].includes(child)) return canAction(state,slug,'sales_orders.edit');
  if (section === 'onboarding') return canAction(state,slug,'company_settings.edit');
  if (section === 'company-profile') return canAction(state,slug,'company_branding.view');
  if (section === 'pricing') {
    if (!child) return membershipFor(state,slug)?.role === 'owner';
    return canAction(state,slug,child === 'access' ? 'assortment.view' : child === 'formation' ? 'purchase_prices.view' : 'sales_prices.view');
  }
  const aliases:Record<string,string>={suppliers:'partners',clients:'partners','link-partners':'partners','link-requests':'partners',manufacturers:'distribution',distributors:'distribution','distribution-points':'distribution','partner-stores':'distribution','wholesale-catalog':'products'};
  return canNavigate(state,slug,aliases[section] || section);
}
